import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, RefreshCw, Download, Plus, Sparkles, AlertTriangle, Wallet, ClipboardCheck, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { billing, openAuthed, fmtMoney, StatusPill } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";
import { ProposalActions, InvoiceActions, PaymentVerifyActions, AskDialog } from "@/components/billing/BillingActions";
import { fileHref } from "@/components/account/accountConstants";
import { CompaniesManager } from "@/components/billing/CompaniesManager";

const TABS = ["overview", "proposals", "invoices", "payments", "catalogue", "companies"];
const th = "px-3 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-slate-500";
const td = "px-3 py-2 align-top text-sm";
const card = "rounded-2xl border border-slate-800 bg-slate-900/40";

const CaseLink = ({ row }) => row.process_id ? (
  <Link to={`/portal/processes/${row.process_id}?tab=billing`} className="text-venka-green-light hover:underline">{row.process_reference || row.process_id.slice(0, 8)}</Link>
) : "—";

const Filter = ({ value, onChange, options }) => (
  <div className="flex flex-wrap gap-1">
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)}
        className={`rounded-full px-3 py-1 text-xs border ${value === v ? "border-venka-green bg-venka-green/15 text-venka-green-light" : "border-slate-700 text-slate-400 hover:text-white"}`}>{l}</button>
    ))}
  </div>
);

// ---------------------------------------------------------------- overview
const Overview = ({ go }) => {
  const { bt } = useBT();
  const [d, setD] = useState(null);
  const load = useCallback(() => billing.overview().then(setD).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  return (
    <div className="space-y-6" data-testid="billing-overview">
      <div className="grid gap-4 sm:grid-cols-3">
        <button type="button" onClick={() => go("payments")} className={`${card} p-4 text-left hover:border-venka-green/50`}>
          <Wallet className="h-5 w-5 text-amber-300 mb-2" /><p className="text-2xl font-bold">{d.pending_payments}</p><p className="text-xs text-slate-400">{bt("toVerify")}</p>
        </button>
        <button type="button" onClick={() => go("proposals", "pending_approval")} className={`${card} p-4 text-left hover:border-venka-green/50`}>
          <ClipboardCheck className="h-5 w-5 text-sky-300 mb-2" /><p className="text-2xl font-bold">{d.proposals_to_approve}</p><p className="text-xs text-slate-400">{bt("toApprove")}</p>
        </button>
        <button type="button" onClick={() => go("proposals", "sent")} className={`${card} p-4 text-left hover:border-venka-green/50`}>
          <Hourglass className="h-5 w-5 text-indigo-300 mb-2" /><p className="text-2xl font-bold">{d.proposals_sent}</p><p className="text-xs text-slate-400">{bt("awaitingCustomer")}</p>
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {Object.entries(d.totals).map(([cur, t]) => (
          <div key={cur} className={`${card} p-4`} data-testid={`billing-totals-${cur}`}>
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">{(d.labels || {})[cur] || cur}</p>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><p className="text-slate-400 text-xs">{bt("outstanding")}</p><p className="font-semibold tabular-nums">{fmtMoney(t.outstanding, cur)}</p></div>
              <div><p className="text-slate-400 text-xs">{bt("overdue")}</p><p className={`font-semibold tabular-nums ${t.overdue > 0 ? "text-red-300" : ""}`}>{fmtMoney(t.overdue, cur)}</p></div>
              <div><p className="text-slate-400 text-xs">{bt("receivedMonth")}</p><p className="font-semibold tabular-nums text-green-300">{fmtMoney(t.received_this_month, cur)}</p></div>
            </div>
          </div>
        ))}
      </div>
      <div className={card}>
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800"><AlertTriangle className="h-4 w-4 text-red-300" /><h3 className="font-semibold text-sm">{bt("overdue")}</h3></div>
        {d.overdue.length === 0 ? <p className="p-4 text-sm text-slate-500">{bt("none")}</p> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr><th className={th}>{bt("number")}</th><th className={th}>{bt("customer")}</th><th className={th}>{bt("case")}</th><th className={th}>{bt("due")}</th><th className={th}>{bt("balance")}</th><th className={th}>{bt("actions")}</th></tr></thead>
            <tbody>{d.overdue.map((i) => (
              <tr key={i.id} className="border-t border-slate-800/60">
                <td className={td}>{i.number}</td><td className={td}>{i.customer?.name}</td><td className={td}><CaseLink row={i} /></td>
                <td className={`${td} text-red-300`}>{(i.due_at || "").slice(0, 10)}</td><td className={`${td} tabular-nums`}>{fmtMoney(i.balance, i.currency)}</td>
                <td className={td}><InvoiceActions inv={i} onChange={load} /></td>
              </tr>))}
            </tbody></table></div>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- proposals
const Proposals = ({ initial }) => {
  const { bt } = useBT();
  const [status, setStatus] = useState(initial || "");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => billing.proposals(status ? { status } : {}).then(setRows).catch(() => setRows([])), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <Filter value={status} onChange={setStatus} options={[["", bt("all")], ["pending_approval", bt("st.pending_approval")], ["draft", bt("st.draft")], ["sent", bt("st.sent")], ["accepted", bt("st.accepted")], ["declined", bt("st.declined")]]} />
      <div className={`${card} overflow-x-auto`}>
        {!rows ? <Loader2 className="m-4 h-5 w-5 animate-spin text-slate-500" /> : rows.length === 0 ? <p className="p-4 text-sm text-slate-500">{bt("none")}</p> : (
          <table className="w-full" data-testid="billing-proposals-table">
            <thead><tr><th className={th}>{bt("number")}</th><th className={th}>{bt("customer")}</th><th className={th}>{bt("case")}</th><th className={th}>{bt("status")}</th><th className={th}>{bt("total")}</th><th className={th}>{bt("actions")}</th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className="border-t border-slate-800/60">
                <td className={td}><div className="font-medium">{p.number || "—"}</div><div className="text-xs text-slate-500">{p.title}</div>
                  {p.source === "ai" && <span className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300"><Sparkles className="h-3 w-3" />{bt("aiDraft")}</span>}</td>
                <td className={td}>{p.customer_name}<div className="text-xs text-slate-500">{p.destination}</div></td>
                <td className={td}><CaseLink row={p} /></td>
                <td className={td}><StatusPill status={p.status} label={bt(`st.${p.status}`)} /><div className="text-[11px] text-slate-500 mt-1">{p.created_by_name}</div></td>
                <td className={`${td} tabular-nums whitespace-nowrap`}>{fmtMoney(p.total, p.currency)}</td>
                <td className={td}><ProposalActions p={p} onChange={load} /></td>
              </tr>))}
            </tbody></table>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- invoices
const Invoices = () => {
  const { bt } = useBT();
  const [filter, setFilter] = useState("open");
  const [entity, setEntity] = useState("");
  const [rows, setRows] = useState(null);
  const params = useCallback(() => {
    const p = {};
    if (filter === "open") p.status = "open,partially_paid";
    if (filter === "overdue") p.overdue = true;
    if (filter === "paid") p.status = "paid";
    if (entity) p.entity = entity;
    return p;
  }, [filter, entity]);
  const load = useCallback(() => billing.invoices(params()).then(setRows).catch(() => setRows([])), [params]);
  useEffect(() => { load(); }, [load]);
  const exportXlsx = () => {
    const q = new URLSearchParams(params()).toString();
    openAuthed(`/billing/invoices/export.xlsx${q ? `?${q}` : ""}`, `venka-invoices-${new Date().toISOString().slice(0, 10)}.xlsx`).catch(() => toast.error("Export failed"));
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-3">
          <Filter value={filter} onChange={setFilter} options={[["open", bt("openOnly")], ["overdue", bt("overdueOnly")], ["paid", bt("st.paid")], ["", bt("all")]]} />
          <Filter value={entity} onChange={setEntity} options={[["", "AO + EU"], ["AO", "AO · AOA"], ["EU", "EU · EUR"]]} />
        </div>
        <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={exportXlsx} data-testid="billing-export"><Download className="h-4 w-4 mr-1" />{bt("exportExcel")}</Button>
      </div>
      <div className={`${card} overflow-x-auto`}>
        {!rows ? <Loader2 className="m-4 h-5 w-5 animate-spin text-slate-500" /> : rows.length === 0 ? <p className="p-4 text-sm text-slate-500">{bt("none")}</p> : (
          <table className="w-full" data-testid="billing-invoices-table">
            <thead><tr><th className={th}>{bt("number")}</th><th className={th}>{bt("customer")}</th><th className={th}>{bt("case")}</th><th className={th}>{bt("status")}</th><th className={th}>{bt("due")}</th><th className={th}>{bt("total")}</th><th className={th}>{bt("balance")}</th><th className={th}>{bt("actions")}</th></tr></thead>
            <tbody>{rows.map((i) => {
              const late = ["open", "partially_paid"].includes(i.status) && i.due_at < new Date().toISOString();
              return (
                <tr key={i.id} className="border-t border-slate-800/60">
                  <td className={td}><div className="font-medium whitespace-nowrap">{i.number}</div><div className="text-xs text-slate-500">{bt(`kind.${i.kind}`)} · {i.entity_code}{i.fiscal_invoice_number ? ` · AGT ${i.fiscal_invoice_number}` : ""}</div></td>
                  <td className={td}>{i.customer_name || i.customer?.name}</td>
                  <td className={td}><CaseLink row={i} /></td>
                  <td className={td}><StatusPill status={i.status} label={bt(`st.${i.status}`)} /></td>
                  <td className={`${td} whitespace-nowrap ${late ? "text-red-300" : ""}`}>{i.kind === "credit_note" ? "—" : (i.due_at || "").slice(0, 10)}</td>
                  <td className={`${td} tabular-nums whitespace-nowrap`}>{fmtMoney(i.totals?.total, i.currency)}</td>
                  <td className={`${td} tabular-nums whitespace-nowrap`}>{i.kind === "credit_note" ? "—" : fmtMoney(i.balance, i.currency)}</td>
                  <td className={td}>{i.kind !== "credit_note" ? <InvoiceActions inv={i} onChange={load} /> : (
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-slate-700 bg-slate-900" onClick={() => openAuthed(`/billing/invoices/${i.id}/pdf`)}>{bt("pdf")}</Button>)}</td>
                </tr>);
            })}
            </tbody></table>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- payments
const Payments = () => {
  const { bt } = useBT();
  const [status, setStatus] = useState("pending_verification");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => billing.payments(status).then((r) => setRows(r.filter((p) => p.status !== "pending_card"))).catch(() => setRows([])), [status]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3">
      <Filter value={status} onChange={setStatus} options={[["pending_verification", bt("st.pending_verification")], ["confirmed", bt("st.confirmed")], ["rejected", bt("st.rejected")], ["", bt("all")]]} />
      <div className={`${card} overflow-x-auto`}>
        {!rows ? <Loader2 className="m-4 h-5 w-5 animate-spin text-slate-500" /> : rows.length === 0 ? <p className="p-4 text-sm text-slate-500">{bt("none")}</p> : (
          <table className="w-full" data-testid="billing-payments-table">
            <thead><tr><th className={th}>{bt("date")}</th><th className={th}>{bt("customer")}</th><th className={th}>{bt("number")}</th><th className={th}>{bt("amount")}</th><th className={th}>{bt("method")}</th><th className={th}>{bt("status")}</th><th className={th}>{bt("actions")}</th></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className="border-t border-slate-800/60">
                <td className={`${td} whitespace-nowrap`}>{(p.created_at || "").slice(0, 10)}</td>
                <td className={td}>{p.customer_name}<div className="text-xs"><CaseLink row={p} /></div></td>
                <td className={td}>{p.invoice_number}</td>
                <td className={`${td} tabular-nums whitespace-nowrap`}>{fmtMoney(p.amount, p.currency)}</td>
                <td className={td}>{bt(`methods.${p.method}`)}{p.reference ? <div className="text-xs text-slate-500">{p.reference}</div> : null}
                  {p.proof_url && <a className="text-xs text-venka-green-light underline" href={fileHref(p.proof_url)} target="_blank" rel="noreferrer">{bt("proof")}</a>}</td>
                <td className={td}><StatusPill status={p.status} label={bt(`st.${p.status}`)} />{p.reject_reason && <div className="text-xs text-red-300 mt-1">{p.reject_reason}</div>}</td>
                <td className={td}><PaymentVerifyActions pay={p} onChange={load} />
                  {p.status === "confirmed" && <Button size="sm" variant="outline" className="h-7 px-2 text-xs border-slate-700 bg-slate-900" onClick={() => openAuthed(`/billing/payments/${p.id}/receipt.pdf`)}>{bt("receipt")}</Button>}</td>
              </tr>))}
            </tbody></table>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------- catalogue
const DESTS = ["any", "canada", "portugal", "usa", "mexico", "brazil", "panama", "france", "uk", "romania", "italy", "netherlands", "ireland", "estonia", "belgium", "germany"];

const Catalogue = () => {
  const { bt, lang } = useBT();
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState(null);
  const [edits, setEdits] = useState({});
  const [ask, setAsk] = useState(null);
  const [curs, setCurs] = useState(["AOA", "EUR"]);
  const load = useCallback(() => billing.catalog({ include_inactive: true }).then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    billing.entities().then((es) => setCurs([...new Set(es.filter((e) => e.active !== false).map((e) => e.currency))])).catch(() => {});
  }, []);
  const savePrice = async (it) => {
    const e = edits[it.id];
    if (!e) return;
    try {
      await billing.updateItem(it.id, { prices: { ...(it.prices || {}), ...e } });
      setEdits((x) => { const y = { ...x }; delete y[it.id]; return y; });
      toast.success(bt("save"));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.toString?.() || "Error");
    }
  };
  const newItem = () => setAsk({
    title: bt("newItem"), cta: bt("save"),
    fields: [
      { key: "code", label: bt("code") },
      { key: "name_pt", label: `${bt("description")} (PT)` }, { key: "name_en", label: `${bt("description")} (EN)` }, { key: "name_fr", label: `${bt("description")} (FR)` },
      { key: "destination", label: bt("destination"), type: "select", default: "any", options: DESTS.map((d) => [d, d]) },
      { key: "category", label: bt("category"), type: "select", default: "service", options: ["service", "government_fee", "extra"].map((c) => [c, bt(`cat.${c}`)]) },
      ...curs.map((c) => ({ key: c, label: `${bt("unitPrice")} ${c}`, type: "number" })),
    ],
    run: (v) => billing.createItem({
      code: v.code, destination: v.destination, category: v.category, vat_exempt: v.category === "government_fee",
      name: { pt: v.name_pt || v.name_en, en: v.name_en || v.name_pt, fr: v.name_fr || v.name_en || v.name_pt },
      prices: Object.fromEntries(curs.filter((c) => v[c] !== "" && v[c] !== undefined).map((c) => [c, Number(v[c])])),
    }),
  });
  return (
    <div className="space-y-3">
      {isAdmin && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" onClick={newItem} data-testid="catalog-new"><Plus className="h-4 w-4 mr-1" />{bt("newItem")}</Button>
          <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={async () => { const r = await billing.loadStarter(); toast.success(`+${r.added}`); load(); }} data-testid="catalog-starter">{bt("loadStarter")}</Button>
        </div>
      )}
      <div className={`${card} overflow-x-auto`}>
        {!rows ? <Loader2 className="m-4 h-5 w-5 animate-spin text-slate-500" /> : rows.length === 0 ? <p className="p-4 text-sm text-slate-500">{bt("none")}</p> : (
          <table className="w-full" data-testid="billing-catalog-table">
            <thead><tr><th className={th}>{bt("description")}</th><th className={th}>{bt("destination")}</th><th className={th}>{bt("category")}</th>{curs.map((c) => <th key={c} className={th}>{bt("unitPrice")} {c}</th>)}<th className={th}>{bt("active")}</th></tr></thead>
            <tbody>{rows.map((it) => (
              <tr key={it.id} className={`border-t border-slate-800/60 ${it.active ? "" : "opacity-50"}`}>
                <td className={td}>{it.name?.[lang] || it.name?.en}<div className="text-[11px] text-slate-500">{it.code}{it.vat_exempt ? ` · ${bt("vatExempt")}` : ""}</div></td>
                <td className={td}>{it.destination}</td>
                <td className={td}>{bt(`cat.${it.category}`)}</td>
                {curs.map((cur) => (
                  <td key={cur} className={td}>
                    {isAdmin ? (
                      <Input type="number" min="0" step="0.01" className="h-8 w-32 bg-slate-950 border-slate-700" placeholder={bt("noPrice")}
                        value={edits[it.id]?.[cur] ?? it.prices?.[cur] ?? ""} onChange={(e) => setEdits((x) => ({ ...x, [it.id]: { ...(x[it.id] || {}), [cur]: e.target.value } }))}
                        onBlur={() => savePrice(it)} data-testid={`price-${it.code}-${cur}`} />
                    ) : (it.prices?.[cur] !== undefined ? fmtMoney(it.prices[cur], cur) : <span className="text-slate-500">{bt("noPrice")}</span>)}
                  </td>
                ))}
                <td className={td}>
                  <input type="checkbox" disabled={!isAdmin} checked={!!it.active} onChange={async (e) => { await billing.updateItem(it.id, { active: e.target.checked }); load(); }} />
                </td>
              </tr>))}
            </tbody></table>
        )}
      </div>
      <AskDialog ask={ask} onClose={(ok) => { setAsk(null); if (ok) load(); }} />
    </div>
  );
};

// ---------------------------------------------------------------- companies
const Companies = () => <CompaniesManager />;

// ---------------------------------------------------------------- page
export const PortalBilling = () => {
  const { bt } = useBT();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [key, setKey] = useState(0);
  const go = (t, status) => setParams(status ? { tab: t, status } : { tab: t });
  return (
    <div className="space-y-5" data-testid="portal-billing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{bt("billing")}</h1>
        <Button size="sm" variant="outline" className="border-slate-700 bg-slate-900" onClick={() => setKey((k) => k + 1)}><RefreshCw className="h-4 w-4" /></Button>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-800">
        {TABS.map((t) => (
          <button key={t} type="button" onClick={() => go(t)} data-testid={`billing-tab-${t}`}
            className={`whitespace-nowrap px-3 py-2 text-sm border-b-2 -mb-px ${tab === t ? "border-venka-green text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
            {bt(t)}
          </button>
        ))}
      </div>
      <div key={`${tab}-${key}`}>
        {tab === "overview" && <Overview go={go} />}
        {tab === "proposals" && <Proposals initial={params.get("status") || ""} />}
        {tab === "invoices" && <Invoices />}
        {tab === "payments" && <Payments />}
        {tab === "catalogue" && <Catalogue />}
        {tab === "companies" && <Companies />}
      </div>
    </div>
  );
};

export default PortalBilling;
